import type { CSSProperties } from 'react';

import type { ColorKey } from '@/lib/site-design/colors';
import { FONT_FAMILY_MAP } from '@/lib/site-design/fonts';
import type { ColorSettings, FontSettings } from '@/types/shared';

export const DESIGN_COLOR_VARIABLE_MAP: Record<ColorKey, string | string[]> = {
  headerBackground: '--header-background',
  headerLinks: '--header-links',
  siteBackground: '--background',
  siteText: '--foreground',
  siteBackgroundAlt: '--muted',
  siteTextAlt: '--muted-foreground',
  primaryButtonBackground: ['--primary', '--accent'],
  primaryButtonText: ['--primary-foreground', '--accent-foreground'],
  secondaryButtonBackground: '--secondary',
  secondaryButtonText: '--secondary-foreground',
  cardBackground: ['--card', '--popover'],
  cardText: ['--card-foreground', '--popover-foreground'],
  borders: ['--border', '--input'],
  footerBackground: '--footer-background',
  footerText: '--footer-text',
  footerLinks: '--footer-links',
};

const DESIGN_COLOR_KEYS = Object.keys(DESIGN_COLOR_VARIABLE_MAP) as ColorKey[];

/** Inline style object: normal React CSS properties plus any `--*` custom properties. */
type CssVarStyle = Record<`--${string}`, string>;
type ThemeStyle = CSSProperties & CssVarStyle;

export function buildCssVariableStyle(
  colors?: Partial<ColorSettings> | null,
): ThemeStyle | undefined {
  if (!colors) return undefined;

  const style: CssVarStyle = {};

  DESIGN_COLOR_KEYS.forEach((key) => {
    const value = colors[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      const cssVarOrList = DESIGN_COLOR_VARIABLE_MAP[key];
      const cssVars = Array.isArray(cssVarOrList) ? cssVarOrList : [cssVarOrList];
      cssVars.forEach((cssVar) => {
        style[cssVar as keyof CssVarStyle] = value;
      });
    }
  });

  if (Object.keys(style).length === 0) return undefined;

  return style as ThemeStyle;
}

export function buildFontVariableStyle(fonts?: FontSettings | null): ThemeStyle | undefined {
  if (!fonts) return undefined;

  const style: CssVarStyle = {};

  if (fonts.headings?.font) {
    const headingFontFamily = FONT_FAMILY_MAP[fonts.headings.font] || fonts.headings.font;
    style['--public-font-heading'] = headingFontFamily;
  }

  if (fonts.headings?.weight) {
    style['--public-heading-weight'] = fonts.headings.weight;
  }

  if (fonts.headings?.capitalization && fonts.headings.capitalization !== 'none') {
    style['--public-heading-transform'] = fonts.headings.capitalization;
  }

  if (fonts.paragraphs?.font) {
    const paragraphFontFamily = FONT_FAMILY_MAP[fonts.paragraphs.font] || fonts.paragraphs.font;
    style['--public-font-paragraph'] = paragraphFontFamily;
  }

  if (fonts.paragraphs?.weight) {
    style['--public-paragraph-weight'] = fonts.paragraphs.weight;
  }

  if (fonts.paragraphs?.size) {
    style['--public-paragraph-size'] = fonts.paragraphs.size;
  }

  if (Object.keys(style).length === 0) return undefined;

  return style as ThemeStyle;
}

export function buildThemeVariables(
  colors?: Partial<ColorSettings> | null,
  fonts?: FontSettings | null,
): ThemeStyle | undefined {
  const colorVariables = buildCssVariableStyle(colors);
  const fontVariables = buildFontVariableStyle(fonts);
  const merged = {
    ...(colorVariables ?? {}),
    ...(fontVariables ?? {}),
  };

  return Object.keys(merged).length === 0 ? undefined : (merged as ThemeStyle);
}
